import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import adminRoles from "./constants/adminRoles.json";
const pageAccessConfig = {
  patients: [...adminRoles, "doctor", "nurse"],
  "create-patient": [...adminRoles, "doctor", "nurse"],
  appointments: [...adminRoles, "doctor", "nurse"],
  "create-appointment": [...adminRoles, "doctor", "nurse"],
  billing: [...adminRoles, "doctor", "nurse"],
  triage: [...adminRoles, "doctor", "nurse"],
  reception: [...adminRoles, "doctor", "nurse"],
  visits: [...adminRoles, "doctor", "nurse"],
  "visit-medical": [...adminRoles,"doctor"],
};

const accessConfig = {
  //get endpoints
  getPatient: [...adminRoles, "doctor", "nurse"],
  getAppointment: [...adminRoles,"doctor", "nurse"],
  getBilling: [...adminRoles,"doctor", "nurse"],
  getTriage: [...adminRoles,"doctor", "nurse"],
  getReception: [...adminRoles,"doctor", "nurse"],
  getVisit: [...adminRoles,"doctor", "nurse"],
  //delete endpoints

  deletePatient: [...adminRoles, "doctor", "nurse"],
  deleteAppointment: [...adminRoles, "doctor", "nurse"],
  deleteBilling: [...adminRoles, "doctor", ],
  deleteTriage: [...adminRoles, "doctor", ],
  deleteReception: [...adminRoles, "doctor", "nurse"],
  deleteVisit: [...adminRoles, "doctor", ],
  deleteWaitingRoom: [...adminRoles, "doctor", "nurse"],
  //put endpoints
  putPatient: [...adminRoles, "doctor", "nurse"],
  putAppointment: [...adminRoles, "doctor", "nurse"],
  putBilling: [...adminRoles, "doctor", ],
  putTriage: [...adminRoles, "doctor", "nurse"],
  putReception: [...adminRoles, "doctor", "nurse"],
  putVisit: [...adminRoles, "doctor", ],
  //post endpoints
  postPatient: [...adminRoles, "doctor", "nurse"],
  postAppointment: [...adminRoles, "doctor", "nurse"],
  postBilling: [...adminRoles, "doctor", "nurse"],
  postTriage: [...adminRoles, "doctor", "nurse"],
  postReception: [...adminRoles, "doctor", "nurse"],
  postVisit: [...adminRoles, "doctor", "nurse"],
};

const resolvePageKey = (pathname = "") => {
  const cleanPath = pathname.split("?")[0].split("#")[0];
  const segments = cleanPath.split("/").filter(Boolean);
  if (!segments.length) return "";

  // Use first segment as the default page key
  const baseKey = segments[0].toLowerCase();
  const aliasMap = {
    patient: "patients",
  };
  const resolvedBaseKey = aliasMap[baseKey] || baseKey;

  // Handle common nested routes like /patients/create
  if (segments.length > 1) {
    const nextSegment = segments[1].toLowerCase();
    const isParam = /^\d+$/.test(nextSegment);
    if (!isParam) {
      const combinedKey = `${resolvedBaseKey}-${nextSegment}`;
      if (pageAccessConfig[combinedKey]) return combinedKey;
    }
  }

  if (pageAccessConfig[resolvedBaseKey]) return resolvedBaseKey;

  const pluralKey = `${resolvedBaseKey}s`;
  if (pageAccessConfig[pluralKey]) return pluralKey;

  return resolvedBaseKey;
};

function usePageAccess(page) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const userRoles = Array.isArray(roles) ? roles : [roles];

  const pageKey = useMemo(() => {
    return page || resolvePageKey(pathname);
  }, [page, pathname]);

  const requiredRoles = useMemo(() => {
    const rolesForPage = pageAccessConfig[pageKey] || [];
    return rolesForPage.flatMap((role) =>
      Array.isArray(role) ? role : [role],
    );
  }, [pageKey]);

  const hasAccess = useMemo(() => {
    return requiredRoles.some((requiredRole) =>
      userRoles.includes(requiredRole),
    );
  }, [requiredRoles, userRoles]);

  useEffect(() => {
    if (!pageKey) return;
    if (!hasAccess) {
      navigate("/");
    }
  }, [hasAccess, navigate, pageKey]);
}

/**
 * Usage:
 * This Function retun true of false based on the access of the user to the page
 * @param {string} endPoint - The API endpoint to check access for.
 * @returns {boolean} - True if the user has access to the endpoint, false otherwise.
 */
function checkAccess(endPoint) {
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const userRoles = Array.isArray(roles) ? roles : [roles];
  const requiredRoles = accessConfig[endPoint] || [];
  const hasAccess = requiredRoles.some((requiredRole) =>
    userRoles.includes(requiredRole),
  );
  return hasAccess;
}

export { usePageAccess, checkAccess };
